import React, { useState, useEffect, useCallback } from 'react';
import { documentsAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';

// Icons mapping based on file type/extension
const getFileIcon = (fileType = '', fileName = '') => {
  const type = (fileType || '').toLowerCase();
  const name = (fileName || '').toLowerCase();
  const ext = name.split('.').pop();
  
  if (type.includes('pdf') || ext === 'pdf') return 'picture_as_pdf';
  if (type.includes('word') || type.includes('docx') || ext === 'docx') return 'description';
  if (type.includes('txt') || type.includes('text') || ext === 'txt') return 'article';
  if (type.includes('md') || ext === 'md') return 'markdown';
  return 'insert_drive_file';
};

const Knowledge = () => {
  const { user } = useAuth();
  const coachId = user?.chatId;

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({}); // Track downloading status per file
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchDocuments = useCallback(async () => {
    if (!coachId) {
       setLoading(false);
       return;
    }
    try {
      const response = await documentsAPI.getAll(coachId);
      // The backend proxies an API that returns { files: [...] } 
      // or it might be raw array depending on the proxy implementation
      const docs = response.data?.files || (Array.isArray(response.data) ? response.data : []);
      setDocuments(docs);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [coachId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const allowedTypes = ['.txt', '.md', '.pdf', '.docx'];
    const validFiles = Array.from(files).filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(ext);
    });

    if (validFiles.length === 0) {
      setError('Only .txt, .md, .pdf, and .docx files are allowed');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      await documentsAPI.upload(coachId, validFiles);
      setSuccess(`Successfully uploaded ${validFiles.length} file(s)`);
      await fetchDocuments();
      event.target.value = ''; // Reset input
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await documentsAPI.delete(coachId, fileId);
      setSuccess('Document deleted');
      await fetchDocuments();
    } catch (err) {
      setError('Error deleting document');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('WARNING: This will permanently delete ALL documents in your knowledge base. Are you absolutely sure?')) return;

    try {
      await documentsAPI.deleteAll(coachId);
      setSuccess('Knowledge base cleared');
      await fetchDocuments();
    } catch (err) {
      setError('Error clearing knowledge base');
    }
  };

  const handleDownload = async (fileId, fileName) => {
    setDownloading(prev => ({ ...prev, [fileId]: true }));
    setError('');
    
    try {
      const response = await documentsAPI.download(coachId, fileId);
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'document');
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download file. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderStatus = (status, errorMsg) => {
    const statusMap = {
      pending: { color: 'bg-amber-500', text: 'Pending', icon: 'schedule' },
      processing: { color: 'bg-blue-500', text: 'Processing', icon: 'sync', animate: 'animate-spin' },
      completed: { color: 'bg-green-500', text: 'Completed', icon: 'check_circle' },
      failed: { color: 'bg-red-500', text: 'Failed', icon: 'error' }
    };

    const s = statusMap[status?.toLowerCase()] || statusMap.pending;

    return (
      <div className="flex items-center gap-2 mt-1" title={errorMsg}>
        <span className={`w-1.5 h-1.5 rounded-full ${s.color}`}></span>
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
          {status === 'processing' && <span className="material-symbols-outlined text-[10px] animate-spin">sync</span>}
          {s.text}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--tangerine)] border-t-transparent shadow-lg shadow-tangerine/20"></div>
      </div>
    );
  }

  if (!coachId) {
      return (
          <div className="flex flex-col items-center justify-center h-[70vh] text-center px-4 animate-in fade-in duration-700">
              <div className="w-24 h-24 bg-[var(--tangerine)]/10 border-[var(--tangerine)]/20">
                  <span className="material-symbols-outlined text-5xl">warning</span>
              </div>
              <h2 className="text-3xl font-black text-gray-900 mb-3 tracking-tight">Configuration Required</h2>
              <p className="text-gray-500 max-w-md text-lg leading-relaxed">
                  Your account isn't fully linked to an AI assistant yet. 
                  Please check your profile settings or contact administrator to set up your Chat ID.
              </p>
          </div>
      )
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col w-full max-w-[1200px] flex-1 animate-in fade-in slide-in-from-bottom-4 duration-1000">
            {/* Header section */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--tangerine)]/10 text-tangerine border-[var(--tangerine)]/20">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-tangerine opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-tangerine"></span>
                  </span>
                  System Online
                </div>
                <h1 className="text-4xl md:text-5xl font-black text-[var(--text-primary)] tracking-tight leading-tight">
                  Knowledge <span className="text-tangerine">Base</span>
                </h1>
                <p className="mt-3 text-[var(--text-secondary)] font-medium text-lg max-w-2xl leading-relaxed">
                  Upload and manage the documents that serve as the brains for your AI coach avatar.
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-4">
                  {documents.length > 0 && (
                      <button 
                          onClick={handleClearAll}
                          className="flex items-center gap-2 px-5 py-3 text-sm font-bold text-red-500 hover:text-red-700 hover:bg-red-50 rounded-2xl transition-all border border-red-50 active:scale-95 shadow-sm"
                      >
                          <span className="material-symbols-outlined text-xl">delete_sweep</span>
                          Clear Repository
                      </button>
                  )}
                  <label className={`
                      flex items-center gap-3 bg-gradient-to-br from-tangerine to-orange-600 
                      hover:from-orange-500 hover:to-orange-700
                      text-white px-8 py-4 rounded-2xl cursor-pointer font-black shadow-xl 
                      shadow-tangerine/30 transition-all hover:-translate-y-1 active:scale-95
                      ${uploading ? 'opacity-70 pointer-events-none' : ''}
                  `}>
                      <span className="material-symbols-outlined text-2xl">add_circle</span>
                      <span className="text-lg">{uploading ? 'Processing...' : 'Add Knowledge'}</span>
                      <input
                          type="file"
                          multiple
                          accept=".txt,.md,.pdf,.docx"
                          onChange={handleFileUpload}
                          disabled={uploading}
                          className="hidden"
                      />
                  </label>
              </div>
            </div>

            {/* Notifications */}
            {error && (
              <div className="mb-8 bg-red-50/80 backdrop-blur-md border-l-4 border-red-500 p-5 rounded-2xl flex items-center gap-4 text-red-700 animate-in slide-in-from-right duration-500">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center text-red-600">
                  <span className="material-symbols-outlined">error</span>
                </div>
                <span className="font-bold flex-1">{error}</span>
                <button onClick={() => setError('')} className="w-8 h-8 rounded-full hover:bg-red-100 flex items-center justify-center transition-colors">
                  <span className="material-symbols-outlined text-xl">close</span>
                </button>
              </div>
            )}
            {success && (
              <div className="mb-8 bg-green-50/80 backdrop-blur-md border-l-4 border-green-500 p-5 rounded-2xl flex items-center gap-4 text-green-700 animate-in slide-in-from-right duration-500">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
                  <span className="material-symbols-outlined">check_circle</span>
                </div>
                <span className="font-bold flex-1">{success}</span>
                <button onClick={() => setSuccess('')} className="w-8 h-8 rounded-full hover:bg-green-100 flex items-center justify-center transition-colors">
                  <span className="material-symbols-outlined text-xl">close</span>
                </button>
              </div>
            )}

            {/* Quick stats grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-[var(--surface-white)] p-8 rounded-[2rem] border border-[var(--border-light)] shadow-xl shadow-black/5 flex items-center gap-6 hover:shadow-2xl transition-all hover:-translate-y-1">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-500">
                      <span className="material-symbols-outlined text-3xl font-bold">folder_open</span>
                  </div>
                  <div>
                      <p className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-1">Knowledge Files</p>
                      <p className="text-3xl font-black text-[var(--text-primary)]">{documents.length}</p>
                  </div>
              </div>
              <div className="bg-[var(--surface-white)] p-8 rounded-[2rem] border border-[var(--border-light)] shadow-xl shadow-black/5 flex items-center gap-6 hover:shadow-2xl transition-all hover:-translate-y-1">
                  <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center text-purple-500">
                      <span className="material-symbols-outlined text-3xl font-bold">verified_user</span>
                  </div>
                  <div>
                      <p className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-1">Coach Context</p>
                      <p className="text-xl font-black text-[var(--text-primary)]">Active & Secure</p>
                  </div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-[2rem] border border-orange-100/50 shadow-xl shadow-black/[0.02] flex items-center gap-6 hover:shadow-2xl transition-all hover:-translate-y-1">
                  <div className="w-16 h-16 bg-[var(--surface-white)] rounded-2xl flex items-center justify-center text-tangerine shadow-sm border border-orange-50">
                      <span className="material-symbols-outlined text-3xl font-bold">psychology</span>
                  </div>
                  <div>
                      <p className="text-xs font-black text-tangerine/60 uppercase tracking-widest mb-1">AI Awareness</p>
                      <p className="text-xl font-black text-[var(--text-primary)]">High Precision</p>
                  </div>
              </div>
            </div>

            {/* Repository Section */}
            <div className="bg-[var(--surface-white)] rounded-[3rem] border border-[var(--border-light)] shadow-2xl shadow-black/5 overflow-hidden">
              <div className="px-10 py-8 border-b border-[var(--border-light)] flex items-center justify-between bg-[var(--surface-white)]">
                  <div>
                      <h3 className="text-2xl font-black text-[var(--text-primary)] tracking-tight">Content Repository</h3>
                      <p className="text-[var(--text-secondary)]/60 text-sm font-medium mt-1">List of all intelligence modules currently loaded</p>
                  </div>
                  <div className="h-12 px-6 flex items-center text-sm font-black text-[var(--text-secondary)] bg-[var(--off-white)] rounded-2xl">
                      {documents.length} MODULES
                  </div>
              </div>

              {documents.length === 0 ? (
                <div className="p-24 text-center flex flex-col items-center">
                  <div className="relative group">
                      <div className="absolute -inset-4 bg-orange-100 rounded-full blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
                      <div className="relative w-32 h-32 bg-[var(--off-white)] rounded-[2.5rem] flex items-center justify-center mb-10 text-gray-200 group-hover:bg-orange-50/50 group-hover:text-tangerine/30 transition-all">
                          <span className="material-symbols-outlined text-7xl font-light">auto_stories</span>
                      </div>
                  </div>
                  <h4 className="text-2xl font-black text-[var(--text-primary)] mb-4 tracking-tight">Your library is empty</h4>
                  <p className="text-[var(--text-secondary)]/60 max-w-sm mx-auto mb-10 text-lg font-medium leading-relaxed">
                    Enable your AI avatar to provide better advice by uploading your professional knowledge and expertise.
                  </p>
                  <label className="bg-gray-900 hover:bg-black text-white px-10 py-4 rounded-2xl cursor-pointer font-black transition-all hover:scale-105 active:scale-95 shadow-2xl shadow-black/20 flex items-center gap-3">
                      <span className="material-symbols-outlined">drive_folder_upload</span>
                      Start Uploading
                      <input type="file" multiple accept=".txt,.md,.pdf,.docx" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-[var(--off-white)]/30">
                        <th className="px-10 py-5 text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-[0.2em] w-1/3">Knowledge Unit</th>
                        <th className="px-10 py-5 text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-[0.2em]">Scale</th>
                        <th className="px-10 py-5 text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-[0.2em]">Category</th>
                        <th className="px-10 py-5 text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-[0.2em]">Deployment</th>
                        <th className="px-10 py-5 text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-[0.2em] text-right w-[100px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {documents.map((doc, idx) => (
                        <tr key={doc.id || idx} className="hover:bg-[var(--off-white)]/50 transition-colors group">
                          <td className="px-10 py-6">
                            <div className="flex items-center gap-5">
                              <div className="w-14 h-14 bg-[var(--off-white)] rounded-[1.2rem] flex items-center justify-center text-[var(--text-secondary)]/60 group-hover:bg-tangerine/5 group-hover:text-tangerine transition-all shadow-sm border border-[var(--border-light)]/50">
                                <span className="material-symbols-outlined text-2xl font-light">
                                  {getFileIcon(doc.type || doc.file_type || doc.extension, doc.file_name || doc.name || doc.filename)}
                                </span>
                              </div>
                              <div className="overflow-hidden">
                                <p className="font-black text-[var(--text-primary)] truncate max-w-[200px] lg:max-w-xs">{doc.file_name || doc.name || doc.filename}</p>
                                {renderStatus(doc.upload_status || 'completed', doc.processing_error)}
                              </div>
                            </div>
                          </td>
                          <td className="px-10 py-6">
                              <span className="text-sm font-black text-[var(--text-secondary)]">
                                  {doc.file_size_bytes ? `${(doc.file_size_bytes / 1024).toFixed(1)} KB` : doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : '--'}
                              </span>
                          </td>
                          <td className="px-10 py-6">
                            <span className="inline-flex items-center px-4 py-1.5 rounded-xl text-[10px] font-black bg-[var(--off-white)] text-gray-600 uppercase tracking-widest border border-[var(--border-light)] shadow-sm">
                              {doc.extension?.replace('.', '') || doc.file_metadata?.extension?.replace('.', '') || doc.type || doc.file_type || doc.name?.split('.').pop() || doc.file_name?.split('.').pop() || 'FILE'}
                            </span>
                          </td>
                          <td className="px-10 py-6">
                            <div className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-gray-300 text-sm italic">event</span>
                              <p className="text-sm font-bold text-[var(--text-secondary)]">{formatDate(doc.created_at || doc.upload_date)}</p>
                            </div>
                          </td>
                          <td className="px-10 py-6 text-right">
                            <div className="flex items-center justify-end gap-2 text-right">
                              <button
                                onClick={() => handleDownload(doc.id, doc.file_name || doc.name || doc.filename)}
                                disabled={downloading[doc.id]}
                                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all active:scale-95 
                                  ${downloading[doc.id] ? 'bg-blue-50 text-blue-300' : 'text-blue-400 hover:bg-blue-50 hover:text-blue-600 group-hover:text-blue-500'}`}
                                title="Download document"
                              >
                                {downloading[doc.id] ? (
                                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <span className="material-symbols-outlined text-xl">download</span>
                                )}
                              </button>
                              <button
                                onClick={() => handleDelete(doc.id)}
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-300 hover:bg-red-50 hover:text-red-500 transition-all active:scale-90 group-hover:text-[var(--text-secondary)]/60"
                                title="Delete Unit"
                              >
                                <span className="material-symbols-outlined text-2xl font-light">delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer support cards */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 pb-10">
              <div className="bg-[var(--surface-white)] p-10 rounded-[3rem] border border-[var(--border-light)] shadow-xl shadow-black/[0.02] flex gap-8 items-start hover:shadow-2xl transition-all">
                  <div className="w-16 h-16 shrink-0 bg-blue-50/50 rounded-3xl flex items-center justify-center text-blue-500">
                      <span className="material-symbols-outlined text-3xl italic">help</span>
                  </div>
                  <div>
                      <h4 className="text-xl font-black text-[var(--text-primary)] mb-3 tracking-tight">Format Guide</h4>
                      <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-4">
                        For optimal AI understanding, ensure your documents are well-formatted. Plain text or Markdown files processed fastest and offer the highest precision.
                      </p>
                      <div className="flex gap-2">
                          {['PDF', 'DOCX', 'TXT', 'MD'].map(f => (
                              <span key={f} className="text-[10px] font-black bg-blue-50 text-blue-500 px-3 py-1 rounded-lg border border-blue-100">{f}</span>
                          ))}
                      </div>
                  </div>
              </div>
              <div className="bg-gradient-to-br from-gray-900 to-black p-10 rounded-[3rem] shadow-2xl shadow-black/20 flex gap-8 items-start hover:-translate-y-1 transition-all">
                  <div className="w-16 h-16 shrink-0 bg-[var(--surface-white)]/10 rounded-3xl flex items-center justify-center text-tangerine backdrop-blur-md">
                      <span className="material-symbols-outlined text-3xl font-bold">auto_awesome</span>
                  </div>
                  <div>
                      <h4 className="text-xl font-black text-white mb-3 tracking-tight">AI Synchronization</h4>
                      <p className="text-[var(--text-secondary)]/60 text-sm leading-relaxed">
                        Your knowledge is automatically indexed and synchronized with your avatar. Changes usually take effect within a few minutes of upload.
                      </p>
                      <button className="mt-6 text-tangerine text-xs font-black uppercase tracking-widest flex items-center gap-2 hover:gap-3 transition-all">
                        Documentation <span className="material-symbols-outlined text-sm">arrow_forward</span>
                      </button>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Knowledge;
